import { Image as ChakraImage } from "@chakra-ui/react";
import { DetailedHTMLProps, ImgHTMLAttributes } from "react";
import { Markdown } from "./Markdown";
import { Text } from "./Text";
import { useColors } from "./useColors";

// What `?as=picture` imports resolve to, via vite-imagetools. Keys of `sources`
// are formats ("avif", "jpeg"); values are srcsets.
type Picture = {
  sources: Record<string, string>;
  img: { src: string; w: number; h: number };
};

// The content column is 50em wide; below that, images are full-bleed.
const sizes = "(min-width: 50em) 50em, 100vw";

const Caption = ({ caption }: { caption?: string }) => {
  const { subtitleColor } = useColors();

  if (!caption) {
    return null;
  }

  return (
    <Text
      as="figcaption"
      color={subtitleColor}
      textAlign="center"
      fontFamily="sans"
      wrap="balance"
      my="parSpace"
    >
      <Markdown content={caption} inline={true} />
    </Text>
  );
};

export const Image = (
  props: Omit<
    DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>,
    "src"
  > & {
    src: string | Picture;
    caption?: string;
  },
) => {
  const { caption, src, ...rest } = props;
  if (typeof src === "string") {
    return (
      <figure>
        <picture>
          <ChakraImage {...rest} src={src} mx="auto" my="parSpace" />
        </picture>
        <Caption caption={caption} />
      </figure>
    );
  }

  return (
    <figure>
      <picture>
        {Object.entries(src.sources).map(([format, srcSet]) => (
          <source
            key={format}
            type={`image/${format}`}
            srcSet={srcSet}
            sizes={sizes}
          />
        ))}
        <ChakraImage
          {...rest}
          src={src.img.src}
          htmlWidth={src.img.w}
          htmlHeight={src.img.h}
          sizes={sizes}
          mx="auto"
          my="parSpace"
        />
      </picture>
      <Caption caption={caption} />
    </figure>
  );
};
